/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Select } from 'antd';
import { Icon } from 'react-inv-forms';
import EditableCell from '../EditableCell';
import TableActionButtons from '../TableActionButtons';
import { CustomTable, Label, InlineButton } from '../../../../styles/sharedUIElemStyles';
import addRed from '../../../../images/addRed.svg';
import { StaticButtonsContainer } from '../../../../styles/tabContainers';
import DataDestructuring from '../../../../helpers/DataDestructuring';
import { PredefinedSelectContainer } from './HoursBilanceTable.style';

const { addKey, addColumnsTranslations, countTotal, createReadOnlyColumns } = DataDestructuring;
const { Option } = Select;
const { Summary } = CustomTable;

/** Budowa obiektu z wartościami predefiniowanymi */
export interface PredefinedItem {
  bilansOpis: string;
  bilansOpisEN: string;
}
export interface Template {
  /** kolumny dla tabeli bilans godzin */
  columns: SingleColumn[];
  /** informacja, czy tabela powinna zawierać wartości predefiniowane */
  predefined?: PredefinedItem[];
  /** zakres liczbowy do inputów typu number */
  rangeHours: { min: number; max: number };
}
export interface Props {
  value: Form.HoursBilanceEffectsFormValue;
  updateFormData: (data: any) => void;
  index: string;
  defaultValue: Form.HoursBilanceEffectsFormValue;
  readOnly: boolean;
  template: Template;
}
/**
 * Komponent tabeli z wartościami predefiniowanymi
 *
 */
export const HoursBilanceTable = (props: Props): React.ReactElement => {
  const { t } = useTranslation();

  const checkIfContainsPredefinedRecords = (data: Form.HoursBilaceData[]): boolean => {
    if (props.template.predefined) {
      const allBilansOpisData = data.map(item => item.bilansOpis);
      const predefined = props.template.predefined.map(item => item.bilansOpis);
      let predefinedRecordsInData = 0;
      predefined.forEach(record => {
        if (allBilansOpisData.includes(record)) predefinedRecordsInData++;
      });
      return predefinedRecordsInData === predefined.length;
    } else return false;
  };
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const [selectDisplayed, showSelect] = useState(false);
  const [isRecordNew, setNewRecord] = useState(false);
  const [disablePredefinedButton, setDisabledPredefinedButton] = useState(checkIfContainsPredefinedRecords(props.value.data));

  const isEditing = (record: any): boolean => record.key === editingKey;
  const edit = (record: any): void => {
    form.setFieldsValue({
      bilansOpis: '',
      bilansOpisEN: '',
      bilansGodz: '',
      ...record,
    });
    setEditingKey(record.key);
  };
  const confirm = async (record: any): Promise<void> => {
    const modifiedCells = await form.validateFields();
    const row = { ...record, ...modifiedCells };
    const newData = addKey(props.value.data);
    const index = newData.findIndex(item => record.key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });
      const updatedValue = { ...props.value, data: newData };
      props.updateFormData(updatedValue);
      setDisabledPredefinedButton(checkIfContainsPredefinedRecords(newData));
      setEditingKey('');
      if (isRecordNew) {
        setNewRecord(false);
      }
    } else {
      setEditingKey('');
    }
  };
  const addNewRecord = (): void => {
    setNewRecord(true);
    showSelect(false);
    const modifiedData = addKey(props.value.data);
    const newRecord = {
      bilansOpis: '',
      bilansOpisEN: '',
      bilansGodz: 1,
      key: props.value.data.length,
      predefined: false,
    };
    modifiedData.push(newRecord);
    const updatedData = { ...props.value, data: modifiedData };
    props.updateFormData(updatedData);
    edit(newRecord);
  };
  const addNewPredefinedRecord = (value: string): void => {
    setNewRecord(true);
    const modifiedData = addKey(props.value.data);
    if (props.template.predefined) {
      const predefinedValue = props.template.predefined.find((item: PredefinedItem) => item.bilansOpis === value);
      const newRecord = {
        bilansOpis: predefinedValue?.bilansOpis,
        bilansOpisEN: predefinedValue?.bilansOpisEN,
        bilansGodz: 1,
        key: props.value.data.length,
        predefined: true,
      };
      modifiedData.push(newRecord);
      const updatedData = { ...props.value, data: modifiedData };
      props.updateFormData(updatedData);
      edit(newRecord);
    }
  };
  const cancelNewRecord = (key: string): void => {
    deleteRecord(key);
    setEditingKey('');
    setNewRecord(false);
  };
  const deleteRecord = (id: string): void => {
    const dataWithKey = addKey(props.value.data);
    const updatedData = dataWithKey.filter(item => item.key !== id);
    const updatedValue = {
      ...props.value,
      data: updatedData,
    };
    props.updateFormData(updatedValue);
    if (disablePredefinedButton) {
      setDisabledPredefinedButton(checkIfContainsPredefinedRecords(updatedData));
    }
  };
  const selectPredefinedRecord = (value: string): void => {
    showSelect(false);
    addNewPredefinedRecord(value);
  };
  const colsWithTln = addColumnsTranslations(props.template.columns);
  const colsWithDefinedWidth: SingleColumn[] = colsWithTln.map(col =>
    col.dataIndex === 'bilansGodz' ? { ...col, width: '10%' } : { ...col, width: '40%' },
  );
  const actionColumns = [...colsWithDefinedWidth];
  actionColumns.push({
    title: '',
    dataIndex: 'actions',
    width: '10%',
    sorter: false,
    // eslint-disable-next-line react/display-name
    render: (_: any, record: any): React.ReactElement => {
      const editable = isEditing(record);
      return (
        <TableActionButtons
          enableEditing={!record.typZajecKodSid || !record.typZajecKodSid.length}
          editable={editable}
          onChangeConfirmation={confirm.bind(null, record)}
          onChangeCancel={isRecordNew ? cancelNewRecord.bind(null, record.key) : setEditingKey.bind(null, '')}
          onEdit={edit.bind(null, record)}
          disabled={editingKey !== ''}
          onDelete={deleteRecord.bind(null, record.key)}
        />
      );
    },
  });

  const mergedColumns = actionColumns.map((col: SingleColumn) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: SingleColumn): SingleColumn => ({
        record,
        inputType:
          (record.predefined && col.dataIndex !== 'bilansGodz') || (record.typZajecKodSid && record.typZajecKodSid.length > 0)
            ? 'disabled'
            : col.type,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        range: props.template.rangeHours,
        sorter: false,
      }),
    };
  });
  const dataWithKey = addKey(props.value.data);
  return (
    <>
      {!props.readOnly ? <Label>{t('BEFORE_CHANGE')}</Label> : null}
      <CustomTable
        dataSource={props.readOnly ? dataWithKey : addKey(props.defaultValue.data)}
        columns={!props.readOnly ? createReadOnlyColumns(colsWithDefinedWidth) : colsWithDefinedWidth}
        rowClassName='editable-row'
        pagination={false}
        locale={{ emptyText: t('NO_DATA') }}
        className={!props.readOnly ? 'table__readOnly' : ''}
        summary={(): React.ReactElement => (
          <Summary.Row>
            <Summary.Cell index={0}>{t('TOTAL')}</Summary.Cell>
            <Summary.Cell index={1}></Summary.Cell>
            <Summary.Cell index={2}>{countTotal(props.defaultValue.data, 'bilansGodz')}</Summary.Cell>
            {!props.readOnly ? <Summary.Cell index={3}></Summary.Cell> : null}
          </Summary.Row>
        )}
      />
      {!props.readOnly ? (
        <>
          <Form form={form} component={false}>
            <PredefinedSelectContainer>
              {props.template.predefined && selectDisplayed ? (
                <>
                  <h3>{t('PREDEFINED_TABLE_CHOOSE_VALUE')}</h3>
                  <Select
                    onChange={selectPredefinedRecord}
                    dropdownClassName='custom_dropdown'
                    className='custom_select'
                    value={undefined}
                    defaultOpen
                    autoFocus
                  >
                    {props.template.predefined.map((item: PredefinedItem) => (
                      <Option
                        key={item.bilansOpis}
                        value={item.bilansOpis}
                        className='custom_option'
                        disabled={props.value.data.some(data => item.bilansOpis === data.bilansOpis)}
                      >
                        {item.bilansOpis} | {item.bilansOpisEN}
                      </Option>
                    ))}
                  </Select>
                </>
              ) : null}
            </PredefinedSelectContainer>
            <CustomTable
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              dataSource={dataWithKey}
              locale={{ emptyText: t('NO_DATA') }}
              columns={mergedColumns}
              rowClassName='editable-row'
              pagination={false}
              summary={(): React.ReactNode => (
                <Summary.Row>
                  <Summary.Cell index={0}>{t('TOTAL')}</Summary.Cell>
                  <Summary.Cell index={1}></Summary.Cell>
                  <Summary.Cell index={2}>{countTotal(props.value.data, 'bilansGodz')}</Summary.Cell>
                  <Summary.Cell index={3}></Summary.Cell>
                </Summary.Row>
              )}
            />
          </Form>
          <StaticButtonsContainer paddingTop={10}>
            <InlineButton onClick={addNewRecord} disabled={editingKey !== ''} data-cy='btnSubjectCardAddHourBalance'>
              <Icon path={addRed} />
              {t('ADD_NEW_RECORD')}
            </InlineButton>
            {props.template.predefined ? (
              <InlineButton
                onClick={(): void => showSelect(!selectDisplayed)}
                disabled={editingKey !== '' || disablePredefinedButton}
                data-cy='btnSubjectCardAddHourBalancePredefined'
              >
                <Icon path={addRed} />
                {t('ADD_NEW__PREDEFINED_RECORD')}
              </InlineButton>
            ) : null}
          </StaticButtonsContainer>
        </>
      ) : null}
    </>
  );
};

export default HoursBilanceTable;
